import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, UsersRound, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TYPES = ["Event", "Holiday", "Reminder"];
const BRANCHES = ["All", "Chennai", "Tada", "Hyderabad", "Jaipur"];
const typeTone = (t) =>
  t === "Holiday" ? "border-red-300 bg-red-50 text-red-700"
  : t === "Reminder" ? "border-amber-300 bg-amber-50 text-amber-700"
  : t === "Birthday" ? "border-pink-300 bg-pink-50 text-pink-700"
  : t === "Anniversary" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
  : t === "Leave" ? "border-violet-300 bg-violet-50 text-violet-700"
  : t === "GST Due" ? "border-orange-300 bg-orange-50 text-orange-700"
  : "border-blue-300 bg-blue-50 text-blue-700";
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function NTCCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: "", time: "", title: "", type: "Event", branch: "All", notes: "" });

  const load = async () => {
    try {
      const res = await api.get("/calendar-events");
      setEvents(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/staff-master").then((r) => setStaff((r.data || []).filter((s) => (s.birthday || s.joining_date) && (s.status || "Active") !== "Inactive"))).catch(() => {});
    api.get("/leave-applications").then((r) => setLeaves((r.data || []).filter((l) => (l.status || "Pending") === "Approved"))).catch(() => {});
  }, []);

  const birthdayEvents = useMemo(() => {
    const mm = String(month + 1).padStart(2, "0");
    return staff
      .filter((s) => s.birthday && String(s.birthday).slice(5, 7) === mm)
      .map((s) => ({
        id: `bday-${s.id}`,
        date: `${year}-${mm}-${String(s.birthday).slice(8, 10)}`,
        time: "",
        title: `${s.name}'s Birthday`,
        type: "Birthday",
        branch: s.branch || "Chennai",
        notes: "",
        virtual: true,
      }));
  }, [staff, month, year]);

  const anniversaryEvents = useMemo(() => {
    const mm = String(month + 1).padStart(2, "0");
    return staff
      .filter((s) => s.joining_date && String(s.joining_date).slice(5, 7) === mm && year > Number(String(s.joining_date).slice(0, 4)))
      .map((s) => {
        const years = year - Number(String(s.joining_date).slice(0, 4));
        return {
          id: `anniv-${s.id}`,
          date: `${year}-${mm}-${String(s.joining_date).slice(8, 10)}`,
          time: "",
          title: `${s.name}'s Work Anniversary (${years} yr${years > 1 ? "s" : ""})`,
          type: "Anniversary",
          branch: s.branch || "Chennai",
          notes: "",
          virtual: true,
        };
      });
  }, [staff, month, year]);

  const leaveEvents = useMemo(() => {
    const monthStart = iso(year, month, 1);
    const monthEnd = iso(year, month, new Date(year, month + 1, 0).getDate());
    const out = [];
    leaves.forEach((l) => {
      const from = l.from_date;
      const to = l.to_date || l.from_date;
      if (!from || to < monthStart || from > monthEnd) return;
      const d0 = new Date(Math.max(new Date(from), new Date(monthStart)));
      const d1 = new Date(Math.min(new Date(to), new Date(monthEnd)));
      for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
        const ds = iso(d.getFullYear(), d.getMonth(), d.getDate());
        out.push({
          id: `leave-${l.id}-${ds}`,
          date: ds,
          time: "",
          title: `${l.employee_name} on Leave${l.leave_type ? ` (${l.leave_type})` : ""}`,
          type: "Leave",
          branch: l.branch || "Chennai",
          notes: l.reason || "",
          virtual: true,
        });
      }
    });
    return out;
  }, [leaves, month, year]);

  const gstEvents = useMemo(() => {
    const pm = new Date(year, month - 1, 1);
    const pmLabel = pm.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    return [
      { id: `gst1-${year}-${month}`, date: iso(year, month, 11), time: "", title: `GSTR-1 due (${pmLabel})`, type: "GST Due", branch: "All", notes: `File GSTR-1 for ${pmLabel} on the GST portal`, virtual: true },
      { id: `gst3b-${year}-${month}`, date: iso(year, month, 20), time: "", title: `GSTR-3B due (${pmLabel})`, type: "GST Due", branch: "All", notes: `File GSTR-3B for ${pmLabel} on the GST portal`, virtual: true },
    ];
  }, [year, month]);

  const byDate = useMemo(() => {
    const m = {};
    [...events, ...birthdayEvents, ...anniversaryEvents, ...leaveEvents, ...gstEvents].forEach((ev) => { (m[ev.date] = m[ev.date] || []).push(ev); });
    return m;
  }, [events, birthdayEvents, anniversaryEvents, leaveEvents, gstEvents]);

  const firstDow = new Date(year, month, 0 + 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayIso = iso(now.getFullYear(), now.getMonth(), now.getDate());

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  const openAdd = (day) => {
    setEditing(null);
    setForm({ date: day ? iso(year, month, day) : iso(year, month, 1), time: "", title: "", type: "Event", branch: "All", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({ date: ev.date || "", time: ev.time || "", title: ev.title || "", type: ev.type || "Event", branch: ev.branch || "All", notes: ev.notes || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Date and title are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/calendar-events/${editing.id}`, form);
        toast.success("Event updated");
      } else {
        await api.post("/calendar-events", form);
        toast.success("Added to NTC Calendar");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/calendar-events/${ev.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const markHolidayAll = async (ev) => {
    if (!window.confirm(`Mark Holiday attendance for ALL staff on ${ev.date.split("-").reverse().join("/")} (${ev.title})?`)) return;
    try {
      const res = await api.post("/attendance/mark-holiday", { event_id: ev.id });
      const { created, skipped } = res.data;
      toast.success(`Holiday marked for ${created} staff${skipped ? ` (${skipped} already had attendance)` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const monthEvents = [...events, ...birthdayEvents, ...anniversaryEvents]
    .filter((ev) => (ev.date || "").startsWith(`${year}-${String(month + 1).padStart(2, "0")}`))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  return (
    <div>
      <PageHeader
        title="NTC Calendar"
        subtitle="Company events, holidays and reminders — monthly view."
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Preload the standard Tamil Nadu festival/holiday list for 2026 & 2027 into the calendar?\n\nExisting holidays on the same dates are kept (no duplicates). You can edit or delete any entry afterwards.")) return;
                try {
                  const res = await api.post("/calendar-events/preload-tn-holidays");
                  toast.success(`TN holidays loaded: ${res.data.created} added${res.data.skipped ? `, ${res.data.skipped} already present` : ""}`);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                }
              }}
              data-testid="preload-tn-holidays-btn"
            >
              <CalendarDays className="mr-2 h-4 w-4" /> Load TN Holidays 26-27
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Preload national holidays (Republic Day 26 Jan, May Day 1 May, Independence Day 15 Aug, Gandhi Jayanthi 2 Oct) for 2026 & 2027?\n\nDates already marked as holidays are kept (no duplicates).")) return;
                try {
                  const res = await api.post("/calendar-events/preload-national-holidays");
                  toast.success(`National holidays loaded: ${res.data.created} added${res.data.skipped ? `, ${res.data.skipped} already present` : ""}`);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                }
              }}
              data-testid="preload-national-holidays-btn"
            >
              <CalendarDays className="mr-2 h-4 w-4" /> Load National Holidays
            </Button>
            <Button onClick={() => openAdd(null)} data-testid="add-calendar-event-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Event
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={prevMonth} data-testid="calendar-prev-month">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-[180px] text-center font-display text-lg font-bold" data-testid="calendar-month-label">
          {MONTHS[month]} {year}
        </div>
        <Button variant="outline" size="icon" onClick={nextMonth} data-testid="calendar-next-month">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="ml-auto flex gap-2">
          {[...TYPES, "Birthday", "Anniversary", "Leave", "GST Due"].map((t) => (
            <Badge key={t} variant="outline" className={typeTone(t)}>{t}</Badge>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid="calendar-grid">
        <div className="grid grid-cols-7 border-b border-border bg-slate-50">
          {DAYS.map((d) => (
            <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            const dIso = day ? iso(year, month, day) : null;
            const evs = dIso ? byDate[dIso] || [] : [];
            return (
              <div
                key={idx}
                className={`min-h-[92px] border-b border-r border-border p-1.5 ${day ? "cursor-pointer hover:bg-slate-50" : "bg-slate-50/50"} ${dIso === todayIso ? "bg-blue-50/60" : ""}`}
                onClick={() => day && openAdd(day)}
                data-testid={day ? `calendar-day-${day}` : undefined}
              >
                {day && (
                  <>
                    <div className={`mb-1 text-xs font-semibold ${dIso === todayIso ? "text-blue-700" : "text-slate-600"}`}>{day}</div>
                    <div className="space-y-1">
                      {evs.map((ev) => (
                        <div key={ev.id} className={`truncate rounded border px-1 py-0.5 text-[10px] font-medium ${typeTone(ev.type)}`} title={`${ev.time ? ev.time + " — " : ""}${ev.title}${ev.notes ? " — " + ev.notes : ""}`}>
                          {ev.time ? `${ev.time} ` : ""}{ev.title}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <h2 className="mb-2 font-display text-base font-bold md:text-lg">This month ({monthEvents.length})</h2>
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {monthEvents.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No events this month. Click a day on the calendar to add one.</div>
          )}
          {monthEvents.map((ev) => (
            <div key={ev.id} className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0" data-testid="calendar-event-row">
              <span className="w-24 shrink-0 text-sm tabular-nums text-slate-600">{ev.date.split("-").reverse().join("/")}</span>
              <span className="w-14 shrink-0 text-xs tabular-nums text-slate-500">{ev.time || "—"}</span>
              <Badge variant="outline" className={`shrink-0 ${typeTone(ev.type)}`}>{ev.type || "Event"}</Badge>
              <span className="text-sm font-medium">{ev.title}</span>
              {ev.branch && ev.branch !== "All" && <span className="text-xs text-muted-foreground">({ev.branch})</span>}
              {ev.notes && <span className="truncate text-xs text-muted-foreground">— {ev.notes}</span>}
              {!ev.virtual && (
              <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" onClick={() => openEdit(ev)} data-testid="edit-calendar-event-btn">
                <Pencil className="h-4 w-4" />
              </Button>
              )}
              {ev.virtual && <span className="ml-auto text-[10px] uppercase tracking-wide text-pink-500">From Staff Master</span>}
              {ev.type === "Holiday" && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700" title="Mark Holiday attendance for all staff" onClick={() => markHolidayAll(ev)} data-testid="mark-holiday-all-btn">
                  <UsersRound className="h-4 w-4" />
                </Button>
              )}
              {!ev.virtual && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(ev)} data-testid="delete-calendar-event-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Calendar Event" : "Add to NTC Calendar"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="field-event-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Time</Label>
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="field-event-time" />
            </div>
            <div className="grid gap-1.5">
              <Label>Title</Label>
              <Input placeholder="e.g. Diwali Holiday / Staff Meeting" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="field-event-title" />
            </div>
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="field-event-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Branch</Label>
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger data-testid="field-event-branch"><SelectValue /></SelectTrigger>
                <SelectContent>{BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Notes</Label>
              <Textarea placeholder="Optional" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="field-event-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-calendar-event-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
